import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, AlertCircle, CheckCircle, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PasswordStrengthMeter({ password, email = '', onChange }) {
  const [strength, setStrength] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (password && password.length > 0) {
      validatePassword();
    } else {
      setStrength(null);
    }
  }, [password]);

  const validatePassword = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/security/password/validate`, null, {
        params: { password, email }
      });
      setStrength(response.data);
      if (onChange) {
        onChange(response.data.is_valid);
      }
    } catch (error) {
      console.error('Error validating password:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!password || password.length === 0) {
    return null;
  }

  const getStrengthColor = (score) => {
    const colors = {
      0: 'bg-red-500',
      1: 'bg-orange-500',
      2: 'bg-yellow-500',
      3: 'bg-lime-500',
      4: 'bg-green-500'
    };
    return colors[score] || 'bg-gray-300';
  };

  const getStrengthLabel = (score) => {
    const labels = {
      0: 'Very Weak',
      1: 'Weak',
      2: 'Fair',
      3: 'Strong',
      4: 'Very Strong'
    };
    return labels[score] || 'Unknown';
  };

  const getStrengthIcon = (score) => {
    if (score <= 1) return <AlertCircle className="h-4 w-4 text-red-500" />;
    if (score === 2) return <Info className="h-4 w-4 text-yellow-500" />;
    if (score === 3) return <Shield className="h-4 w-4 text-lime-500" />;
    return <CheckCircle className="h-4 w-4 text-green-500" />;
  };

  if (loading || !strength) {
    return (
      <div className="mt-2 text-sm text-gray-500">
        Checking password strength...
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Strength Bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getStrengthColor(strength.score)}`}
            style={{ width: `${(strength.score + 1) * 20}%` }}
          />
        </div>
        <div className="flex items-center gap-1">
          {getStrengthIcon(strength.score)}
          <span className={`text-sm font-medium ${
            strength.score <= 1 ? 'text-red-600' :
            strength.score === 2 ? 'text-yellow-600' :
            strength.score === 3 ? 'text-lime-600' :
            'text-green-600'
          }`}>
            {getStrengthLabel(strength.score)}
          </span>
        </div>
      </div>

      {/* Feedback Messages */}
      {strength.feedback && strength.feedback.length > 0 && (
        <div className="space-y-1">
          {strength.feedback.map((message, index) => (
            <div
              key={index}
              className={`text-xs flex items-start gap-1 ${
                message.includes('must') || message.includes('should not')
                  ? 'text-red-600'
                  : message.includes('Strong')
                  ? 'text-green-600'
                  : 'text-gray-600'
              }`}
            >
              <span className="mt-0.5">
                {message.includes('must') || message.includes('should not') ? '✗' : 
                 message.includes('Strong') ? '✓' : '•'}
              </span>
              <span>{message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
